import { debounce } from './theme.js';

export class CollectionInfo extends HTMLElement {
  constructor() {
    super();
    this.debounceOnChange = debounce((event) => this.onChangeHandler(event), 800);
    this.addEventListener('change', this.debounceOnChange.bind(this));
    this.addEventListener('click', this.onClickHandler.bind(this));
  }

  onClickHandler = (event) => {
    const filterButton = event.target.closest('[data-render-section-url]');
    if (filterButton) {
      event.preventDefault();
      const searchParams = new URLSearchParams(filterButton.dataset.renderSectionUrl.split('?')[1]).toString();
      this.fetchSection(searchParams);
    }
  };

  onChangeHandler = (event) => {
    if (!event.target.matches('[data-render-section]')) return;

    const form = event.target.closest('form') || document.querySelector('#filters-form') || document.querySelector('#filters-form-drawer');
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    const existingParams = new URLSearchParams(window.location.search);
    const qValue = existingParams.get('q');

    this.removeDefaultPriceFilters(searchParams, form);

    const finalParams = qValue ? `q=${encodeURIComponent(qValue)}&${searchParams}` : searchParams.toString();
    this.fetchSection(finalParams);
  };

  get form() {
    return this.querySelector('collection-info form');
  }

  updateSourceFromDestination = (html, id) => {
    const source = html.getElementById(`${id}`);
    const destination = this.querySelector(`#${id}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  };

  removeDefaultPriceFilters = (searchParams, form) => {
    const priceMin = searchParams.get('filter.v.price.gte');
    const priceMax = searchParams.get('filter.v.price.lte');
    const actualMaxPrice = form.querySelector('.max-range')?.getAttribute('max');

    const isMinAtDefault = priceMin === '0' || !priceMin;
    const isMaxAtDefault = !actualMaxPrice || priceMax === actualMaxPrice || !priceMax;

    if (isMinAtDefault && isMaxAtDefault) {
      searchParams.delete('filter.v.price.gte');
      searchParams.delete('filter.v.price.lte');
    }
  };

  updateURL(searchParams) {
    history.pushState({}, '', `${window.location.pathname}?${searchParams}`);
  }

  updateFilters = (html, className) => {
    const filtersFromFetch = html.querySelectorAll(`collection-info .${className}`);
    const filtersFromDom = document.querySelectorAll(`collection-info .${className}`);

    // Remove filters that are no longer returned from the server
    Array.from(filtersFromDom).forEach((currentElement) => {
      if (!Array.from(filtersFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    Array.from(filtersFromFetch).forEach((elementToRender, index) => {
      document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
    });
  };

  setDisplay = (id, value) => {
    const el = this.querySelector(`#${id}-${this.dataset.section}`);
    if (el) el.style.display = value;
  };

  showLoadingOverlay = () => {
    this.setDisplay('loading-overlay', 'flex');
    this.setDisplay('loading-spinner', 'block');
    this.setDisplay('results-count', 'none');
    this.setDisplay('drawer-results-count', 'none');
    this.setDisplay('drawer-loading-spinner', 'block');
  };

  hideLoadingOverlay = () => {
    this.setDisplay('loading-overlay', 'none');
    this.setDisplay('loading-spinner', 'none');
    this.setDisplay('results-count', 'block');
    this.setDisplay('drawer-results-count', 'block');
    this.setDisplay('drawer-loading-spinner', 'none');
  };

  revealProductStars = (root) => {
    if (!root) return;
    root.querySelectorAll('.product__card-stars, .lp-product-card__reviews').forEach((wrap) => {
      const countEl = wrap.querySelector('.junip-product-summary-review-count');
      if (!countEl) return;
      if (!(countEl.textContent || countEl.innerHTML || '').trim()) return;
      wrap.style.opacity = '1';
    });
  };

  reloadJunipStars = (root) => {
    if (window.junip && typeof window.junip.refreshWidgets === 'function') {
      try {
        window.junip.refreshWidgets();
      } catch (e) {}
    }
    if (window.junip && typeof window.junip.storefrontStarsLoader === 'function') {
      try {
        window.junip.storefrontStarsLoader();
      } catch (e) {}
    } else if (window.junipLoader && typeof window.junipLoader.load === 'function') {
      try {
        window.junipLoader.load();
      } catch (e) {}
    }
    this.revealProductStars(root);
  };

  scheduleJunipRefresh = () => {
    const sectionRoot = document.getElementById(`shopify-section-${this.dataset.section}`);
    const root = sectionRoot || this;
    this.reloadJunipStars(root);
    setTimeout(() => this.reloadJunipStars(root), 300);
  };

  fetchSection = (searchParams) => {
    this.showLoadingOverlay();
    fetch(`${window.location.pathname}?section_id=${this.dataset.section}&${searchParams}`)
      .then((response) => response.text())
      .then((responseText) => {
        let html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateURL(searchParams);
        this.updateSourceFromDestination(html, `product-grid-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `drawer-results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filters-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filter-group-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `js-clear-filters-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-drawer-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `filters-drawer-buttons-wrapper-id`);
        this.updateFilters(html, `js-filter`);
        this.hideLoadingOverlay();
        this.scheduleJunipRefresh();
        document.dispatchEvent(new CustomEvent('theme:page:load', {
          detail: { sectionEl: document.getElementById(`shopify-section-${this.dataset.section}`) },
        }));
      })
      .catch((error) => {
        console.error(error);
        this.hideLoadingOverlay();
      });
  };
}

if (!customElements.get('collection-info')) {
  customElements.define('collection-info', CollectionInfo);
}